import * as THREE from 'three';
import { LoadResult, loadedFilesStore } from './loaders';
import { resolveTextureUrl, preprocessTextureName } from './textureResolvers';

// Configuration constant to enable/disable auto texture loading
export const ENABLE_AUTO_TEXTURE_LOAD = true;

function materialHasMap(m: THREE.Material): m is THREE.Material & { map: THREE.Texture | null } {
  return 'map' in m;
}

function hasUsableUvs(geometry: THREE.BufferGeometry): boolean {
  if (!geometry.hasAttribute('uv')) {
    return false;
  }

  const uv = geometry.getAttribute('uv');
  if (!uv || uv.itemSize < 2 || uv.count < 3) {
    return false;
  }

  let minU = Number.POSITIVE_INFINITY;
  let maxU = Number.NEGATIVE_INFINITY;
  let minV = Number.POSITIVE_INFINITY;
  let maxV = Number.NEGATIVE_INFINITY;

  const sampleCount = Math.min(uv.count, 256);
  for (let i = 0; i < sampleCount; i++) {
    const u = uv.getX(i);
    const v = uv.getY(i);

    if (!Number.isFinite(u) || !Number.isFinite(v)) {
      continue;
    }

    minU = Math.min(minU, u);
    maxU = Math.max(maxU, u);
    minV = Math.min(minV, v);
    maxV = Math.max(maxV, v);
  }

  if (!Number.isFinite(minU) || !Number.isFinite(minV)) {
    return false;
  }

  const epsilon = 1e-6;
  return maxU - minU > epsilon || maxV - minV > epsilon;
}

export async function autoLoadTextures(
  loadResult: LoadResult,
  sourceUrl: string,
  onProgress: (msg: string) => void,
  onWarning: (msg: string) => void,
) {
  if (!ENABLE_AUTO_TEXTURE_LOAD) {
    return;
  }

  const materialsToLoad = new Map<string, THREE.Material[]>();

  const processObject = (object: THREE.Object3D) => {
    object.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        // Only process meshes that have usable UVs
        if (!hasUsableUvs(child.geometry)) {
          return;
        }

        const meshMaterials = Array.isArray(child.material) ? child.material : [child.material];

        meshMaterials.forEach((material) => {
          // Check if material can have texture and doesn't have one
          if (materialHasMap(material) && !material.map) {
            const name = material.name;
            if (!name) return;

            const existing = materialsToLoad.get(name);
            if (existing) {
              if (!existing.includes(material)) {
                existing.push(material);
              }
            } else {
              materialsToLoad.set(name, [material]);
            }
          }
        });
      }
    });
  };

  if (loadResult.object) {
    processObject(loadResult.object);
  }
  if (loadResult.staticObject) {
    processObject(loadResult.staticObject);
  }

  const materialNames = Array.from(materialsToLoad.keys());
  if (materialNames.length === 0) {
    return;
  }

  console.info(
    `[AutoTextureLoader] Found ${materialNames.length} materials without textures and with usable UVs. Attempting to auto-load.`,
  );

  let loadedCount = 0;
  const total = materialNames.length;

  for (let i = 0; i < total; i++) {
    const name = materialNames[i];
    const materials = materialsToLoad.get(name)!;
    // Try to load texture with material name (preprocess to handle .psd -> .png conversion)
    let filename = `${name}.png`;
    filename = preprocessTextureName(filename);
    const textureUrl = resolveTextureUrl(filename, sourceUrl);

    onProgress(`Auto-loading textures: ${i + 1}/${total}`);

    try {
      const loader = new THREE.TextureLoader();
      const texture = await loader.loadAsync(textureUrl);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.name = filename; // Set name for debugging/listing

      let updatedCount = 0;
      materials.forEach((material) => {
        if (materialHasMap(material)) {
          material.map = texture;
          material.needsUpdate = true;
          updatedCount++;
        }
      });

      // Track the auto-loaded texture URL for export
      loadedFilesStore.fileUrlCache.set(filename, textureUrl);

      console.info(
        `[AutoTextureLoader] Successfully loaded texture for material "${name}" -> used "${filename}" (${updatedCount} instances updated)`,
      );
      loadedCount++;
    } catch (error) {
      // Just warn, don't throw
      const msg = `[AutoTextureLoader] Failed to auto-load texture "${filename}" for material "${name}"`;
      console.warn(msg, error);
      onWarning(msg);
    }
  }

  if (loadedCount > 0) {
    onProgress(`Auto-loaded ${loadedCount} texture(s)`);
  }
}
